import { Tooltip } from '@mui/material'
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro'
import { sortBy } from 'lodash'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { Feature_Config, Kap_Measure_Kpi_Annual_Report, Query_Root } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { fetchKapProgramYearlyReportsKpi } from 'src/screens/kap/shared/measure/kapMeasureYearlyKpisQueries'
import { LEVEL_TYPE, PROCESS } from 'src/shared/constants/constants'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { S } from 'src/shared/styled/S'
import { DataGridUtils } from 'src/shared/utils/DataGridUtils'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { muiTheme } from 'src/theme/theme'
import { useUserLocale } from 'src/user/UserContext'
import { useClient } from 'urql'

type MeasureFeaturesKpiWithYears = {
  featureType: string
  feature: string
  featureTooltip?: string
  otherDescription?: string
  total?: number | null | undefined
} & { [key: number]: number | null | undefined }

interface Props {
  measureId: number
  measureLevel: LEVEL_TYPE
  gridReload?: boolean
}

const createMeasureFeatureKpisWithYears = (
  kpis: Kap_Measure_Kpi_Annual_Report[],
  allFeaturesAvailable: Feature_Config[],
): MeasureFeaturesKpiWithYears[] => {
  if (kpis.length === 0) {
    return []
  }

  const sortedFeaturesAvailable = sortBy(allFeaturesAvailable, [
    'feature_type_config.feature_type_sorting_configs[0].sort_order',
    'feature_group_config.feature_group_sorting_configs[0].sort_order',
    'feature_sorting_configs[0].sort_order',
  ])

  // list all of the years for the kpis as { 2022: null, 2023: null}
  const allYearsAvailableObject: { [key: number]: number | null | undefined } = {}
  kpis.forEach((kpi) => {
    const year = kpi.milestone.year_in_focus as number
    allYearsAvailableObject[year] = null
  })

  const featureToYearValueMap = new Map<string, MeasureFeaturesKpiWithYears>()

  // fill features in map with empty values for years
  sortedFeaturesAvailable.forEach((feature) => {
    featureToYearValueMap.set(feature.names, {
      featureType: feature.feature_type_config.names,
      featureTooltip: feature.tooltips,
      otherDescription: feature.feature_values?.[0]?.other_description ?? undefined,
      ...allYearsAvailableObject,
      feature: feature.names,
    })
  })

  // fill features with values per year
  kpis.forEach((kpi) => {
    const featureName = kpi.feature_config.names

    const year = kpi.milestone.year_in_focus
    const value = kpi.actual_value

    const yearlyValueForFeature = featureToYearValueMap.get(featureName)
    if (yearlyValueForFeature) {
      yearlyValueForFeature[year] = value
    }
  })

  const featureKpisWithYears: MeasureFeaturesKpiWithYears[] = []

  featureToYearValueMap.forEach((value) => {
    const { featureType, featureTooltip, otherDescription, feature, ...yearsAndValues } = value

    const allValues = Object.values(yearsAndValues)
    const allValuesEmpty = allValues.every((value) => value == null)

    const featureKpiWithYear: MeasureFeaturesKpiWithYears = {
      feature: feature,
      featureType: value.featureType,
      featureTooltip: value.featureTooltip,
      otherDescription: value.otherDescription,
      ...yearsAndValues,
      total: allValuesEmpty ? null : DataGridUtils.sum(allValues),
    }

    featureKpisWithYears.push(featureKpiWithYear)
  })

  return featureKpisWithYears
}

const getAllYearsForKpis = (kpisPerYear: MeasureFeaturesKpiWithYears[]): number[] => {
  if (kpisPerYear.length === 0) {
    return []
  }
  const kpi = kpisPerYear[0]

  const { feature, featureType, featureTooltip, otherDescription, total, ...years } = kpi
  const yearsArray = Object.keys(years).sort() as unknown as number[]
  return yearsArray
}

export const KapMeasureFeaturesYearlyKpiGrid = ({ measureId, measureLevel, gridReload }: Props): ReactElement => {
  const gridTranslations = useGridTranslateHook()
  const locale = useUserLocale()
  const { getMessage } = useMessageSource()
  const apiRef = useGridApiRef()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [featuresKpiWithYears, setFeaturesKpiWithYears] = useState<MeasureFeaturesKpiWithYears[]>([])

  const allYears = getAllYearsForKpis(featuresKpiWithYears)

  const rowsEmpty = featuresKpiWithYears.length === 0 ? true : false

  const featureConfigSortFilter = {
    level: {
      _eq: measureLevel,
    },
  }

  const fetchData = useCallback(async () => {
    const { data } = await urqlClient
      .query<{
        kap_measure_kpi_annual_report: Query_Root['kap_measure_kpi_annual_report']
        feature_config: Query_Root['feature_config']
      }>(fetchKapProgramYearlyReportsKpi, {
        measureId: measureId,
        language: locale,
        level: measureLevel,
        process: PROCESS.KAP,
        featureTypeSortingConfig: featureConfigSortFilter,
        featureGroupSortingConfig: featureConfigSortFilter,
        featureSortingConfig: featureConfigSortFilter,
      })
      .toPromise()

    if (data) {
      const kpis = data.kap_measure_kpi_annual_report
      const allFeaturesAvailable = data.feature_config

      const featureKpisWithYears = createMeasureFeatureKpisWithYears(kpis, allFeaturesAvailable)

      setFeaturesKpiWithYears(featureKpisWithYears)
    } else {
      notificationService.operationFailed()
    }
  }, [urqlClient, measureId, locale, measureLevel, notificationService, gridReload])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const columns = useMemo<GridColDef[]>(
    () =>
      !rowsEmpty
        ? [
            {
              field: 'featureType',
              headerName: getMessage('label.feature.type'),
              sortable: false,
              flex: 0.6,
              type: 'string',
              minWidth: 200,
              renderCell({ value }) {
                return (
                  <S.DataGrid.Typography variant="subtitle2" sx={{ color: muiTheme.palette.text.disabled }}>
                    {value}
                  </S.DataGrid.Typography>
                )
              },
            },
            {
              field: 'feature',
              headerName: getMessage('label.feature'),
              sortable: false,
              type: 'string',
              flex: 0.9,
              minWidth: 200,
              renderCell: (params) => {
                let featureName = ''
                const value = params.value
                if (value) {
                  // for "Other" features display also the corresponding description
                  featureName = params.row.otherDescription ? `${value}: ${params.row.otherDescription}` : value
                }
                return (
                  <S.DataGrid.CenterDataGridCell>
                    <S.Span.TextOverflow $withTooltip>{value && featureName}</S.Span.TextOverflow>
                    {params.row.featureTooltip && (
                      <Tooltip placement="right" title={params.row.featureTooltip}>
                        <S.Icons.GridTooltipInfo />
                      </Tooltip>
                    )}
                  </S.DataGrid.CenterDataGridCell>
                )
              },
            },
            ...allYears.map(
              (y) =>
                ({
                  field: `${y}`,
                  headerName: `${y}`,
                  sortable: false,
                  flex: 0.3,
                  minWidth: 120,
                  type: 'number',
                  renderCell: ({ value }) => {
                    if (value == null || value === '' || Number.isNaN(value)) {
                      return '-'
                    }
                    return <S.Span.TextOverflow>{value?.toLocaleString('de-CH')}</S.Span.TextOverflow>
                  },
                }) as GridColDef,
            ),
            {
              field: 'actualsToDate',
              headerName: getMessage('label.actuals.to.date'),
              sortable: false,
              flex: 0.3,
              type: 'number',
              minWidth: 120,
              renderCell: ({ value }) => {
                if (value == null || value === '' || Number.isNaN(value)) {
                  return '-'
                }
                return <S.Span.TextOverflow>{value?.toLocaleString('de-CH')}</S.Span.TextOverflow>
              },
            },
          ]
        : [],
    [rowsEmpty, getMessage, allYears],
  )

  const rows = useMemo(
    () =>
      featuresKpiWithYears.map((item, index) => {
        const { featureType, feature, total, featureTooltip, otherDescription, ...years } = item
        return {
          id: index,
          featureType: item.featureType,
          feature: item.feature,
          featureTooltip: item.featureTooltip,
          otherDescription: item.otherDescription,
          ...years,
          actualsToDate: item.total,
        }
      }),
    [featuresKpiWithYears],
  )

  return (
    <DataGridContainer>
      <S.DataGrid.ViewEditMode
        $viewMode
        apiRef={apiRef as any}
        rows={rows}
        columns={columns}
        localeText={gridTranslations}
        disableColumnSelector
        disableColumnReorder
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnFilter
        disableMultipleColumnsSorting
        columnHeaderHeight={rowsEmpty ? 0 : undefined}
        hideFooter
      />
    </DataGridContainer>
  )
}
